/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';

// tslint:disable: only-arrow-functions
// tslint:disable: no-redundant-jsdoc

/// <reference types="@types/jquery" />

import { Point, Rectangle } from 'pixi.js-legacy';

import { Observable, Subscriber, EMPTY, concat } from 'rxjs';
import { scan } from 'rxjs/operators';

import { Ticket } from '@touchplan/common';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { ListLike } from 'src/app/app-data/interfaces/list-like';
import { logger } from '@touchplan/logging';

declare const window;
declare const Intercom: any;

if ( Map ) {
	(<any> Map.prototype).first = function() {
		logger.log('[ticket-utils] [Map.first] debug code, don\'t use');
		return this.size > 0 ? this.values().next().value : null;
	};
}

export function shiftWeekStartToMonday(dayOfWeek) {
	if (dayOfWeek === 0) { return 6; }
	return dayOfWeek - 1;
}

// Fixed point function combinator
export function Y(f) {
	return (function(g) { return g(g); })(function(h) {
		return function() { return f(h(h)).apply(null, arguments); };
	});
}

//### snapping(x, list, stickiness, comp, ctx)
//generic function for calculating proximity to things within a list on a single dimension.
// In other words, it finds the list item closest to x.
//* x - number to test against [required]
//* list - an array of values to compare against [required]
//* stickiness - number that indicates the range of the culling function [required]
//* comp - a callback function, is passed the current list item, should return the numeric value that list item represents.
// Usually it's a property on the item. [required]
//* ctx - the context to execute with [optional]
//#### returns
//an object with two properties:
//* loc - the numeric value detected as the closest to x
//* item - the list item loc corresponds to
export const snapping = function (x, list, stickiness, comp, ctx?) {
	const matches = [];

	function addMatch(x, item) {
		const obj = {
			loc: x,
			item
		};
		matches.push(obj);
		return obj;
	}

	function cull(subList, subComp) {
		subList.forEach(function(item) {
			const result = Number(subComp.call(ctx, item));
			if (isNaN(result)) { return; }

			if (x > result - stickiness && x < result + stickiness) {
				addMatch(result, item);
			}
		}, ctx);
	}

	if (Array.isArray(comp)) {
		if (list.length === comp.length) {
			for (let i = 0; i < comp.length; i++) {
				cull(list[i], comp[i]);
			}
		}
		else {
			logger.log('multi-src mode requires the list and location function to have the same number of args');
		}
	}
	else {
		cull(list, comp);
	}

	//logger.log('matches', matches);
	if (matches.length === 0) { return false; }
	if (matches.length === 1) { return matches[0]; }

	let closest;
	let range = Infinity;
	matches.forEach(function(col) {
		const diff = Math.abs(x - col.loc);
		if (diff < range) {
			range = diff;
			closest = col;
		}
	});
	return closest;
};

/**
 * ripped from rxjs 5.5.6 and modified slightly
 */
export const subscribeToPromise = <T>(promise: PromiseLike<T>, subscriber: Subscriber<T>) => {
	promise.then(
		(value) => {
			if (!subscriber.closed) {
				subscriber.next(value);
				subscriber.complete();
			}
		},
		(err: any) => subscriber.error(err)
	);
	return subscriber;
};

/**
 * Accepts a list of functions that are expected to return Promises.
 * Sets up an internal queue when the next promise can't execute until
 * the previous has resolved.
 * Returns an accumulated observable of all the results.
 * @param  list
 * @return     [description] Observable<Array<T>>
 */
export function sequencePopups<T= any>(list: Array<() => Promise<T>>): Observable<T[]> {
	let returnObs: Observable<T>;
	if (!list || !list.length) { return EMPTY; }
	list.forEach((cb) => {
		const ob = new Observable<T>((subscriber) => {
			subscribeToPromise(cb(), subscriber);
		});
		if (!returnObs) { returnObs = ob; }
		else { returnObs = concat( returnObs, ob); }
	});
	return returnObs.pipe(
		scan((acc, curr: T) => {
			return [...acc, curr];
		}, [])
	);
}

/**
 * MOC-2641. Utility function to check if current user supports WebGL.
 * Factors in experimental tag.
 * This is the suggested way for checking for WebGL according to https://get.webgl.org/.
 * @returns {boolean}
 */
export function isWebGLEnabled(): boolean {
	const canvas = document.createElement('canvas');
	let gl;
	// Check to see if WebGL rendering context is available without a flag
	try { gl = canvas.getContext('webgl'); }
	catch (x) { gl = null; }

	// Check to see if WebGL is available _with_ an experimental flag.
	if (gl === null) {
		try { gl = canvas.getContext('experimental-webgl'); }
		catch (x) { gl = null; }
	}

	return !!(gl);

}

/**
 * Method to floor a number
 * @param {number} p
 */
export function snapPixel(p: number): number {
	return Math.floor(p);
}

export function getCirclePoint( p: Point, angle: number, width: number, height: number ) {
	const vy = Math.sin(angle);
	const vx = Math.cos(angle);
	const sides: any = { };
	const rad = 0.50;
	const offset = Math.sqrt(rad * (width * width + height * height));
	sides.left = (-width / 2) + offset;
	sides.right = (width / 2) + offset;
	sides.top = (-height / 2) + offset;
	sides.bottom = (height / 2) + offset;
	let min;
	for (const side in sides) { if (sides[side] > 0 && (!min || sides[side] < sides[min])) { min = side; } }
	const t = sides[min];
	return new Point(p.x + t * vx, p.y + t * vy);
}

export function getRectPoint( p: Point, angle: number, width: number, height: number ) {
	const vy = Math.sin(angle);
	const vx = Math.cos(angle);

	const sides: any = { };
	sides.left = (-width / 2) / vx;
	sides.right = (width / 2) / vx;
	sides.top = (-height / 2) / vy;
	sides.bottom = (height / 2) / vy;

	let min;
	for (const side in sides) { if (sides[side] > 0 && (!min || sides[side] < sides[min])) { min = side; } }
	const t = sides[min];
	return new Point(p.x + t * vx, p.y + t * vy);
}

interface LineFunction {
	m: number;
	b: number;
}

function getLineFunction(pt1: Point, pt2: Point) {
	const m = (pt2.y - pt1.y) / (pt2.x - pt1.x);
	const b = pt1.y - m * pt1.x;
	return { m, b};
}

function getEdge(startLine: LineFunction, p: Point, width: number, height: number) {
	const ptTop = new Point(p.x, p.y - height / 2);
	const ptLeft = new Point(p.x - width / 2, p.y);
	const ptRight = new Point(p.x + width / 2, p.y);
	const ptBottom = new Point(p.x, p.y + height / 2);

	const rangeRect = new Rectangle(ptLeft.x - 1, ptTop.y - 1, ptRight.x - ptLeft.x + 2, ptBottom.y - ptTop.y + 2);

	const lines = [
		getLineFunction(ptLeft, ptTop),
		getLineFunction(ptTop, ptRight),
		getLineFunction(ptLeft, ptBottom),
		getLineFunction(ptBottom, ptRight)
	];
	// logger.log('starting points', ptTop,ptLeft,ptRight,ptBottom);
	const mappedLines = [];
	 lines.forEach((lf, idx) => {
		const x = (startLine.b - lf.b) / (lf.m - startLine.m);
		const y = lf.m * x + lf.b;

		if (rangeRect.contains(x, y)) {
			mappedLines.push(new Point(x, y));
		}
	});
	// logger.log('mappedLines', mappedLines, lines);
	return mappedLines;

}

export function getDiamondPoint( rawP: Point, angle: number, endP: Point, width: number, height: number ) {
	// logger.log('startPoint', p);
	const diamondRenderSize = 0;

	const p = new Point(rawP.x, rawP.y);

	const m = Math.tan(angle);
	// var m = (endP.y - p.y)/(endP.x - p.x);
	const b = p.y - m * p.x;

	const newPoint = getEdge({ m, b}, p, width + diamondRenderSize, height + diamondRenderSize).reduce((acc, lf) => {
		const x = lf.x;
		const y = lf.y;

		const distance = Math.sqrt( Math.pow(x - endP.x, 2) + Math.pow(y - endP.y, 2) );
		if (distance < acc.distance) { return { distance, pt: new Point(x, y)}; }
		else { return acc; }
	}, { distance: Infinity, pt: new Point(0, 0)});
	return newPoint.pt;
}

export function calcAngle(sPt: Point, ePt: Point) {
	return Math.atan2(ePt.y - sPt.y, ePt.x - sPt.x);
}

export function getCenterOffsetPoint( sPt: Point, ePt: Point, width: number, height: number, type?: string ) {
	if ( type === 'constraint') {
		return getCirclePoint( sPt, calcAngle(sPt, ePt), width, height );
	}
	else if (type === 'milestone') {
		//logger.log('need to implement diamonds...');
		// return this.getCirclePoint( sPt, Math.atan2(ePt.y - sPt.y, ePt.x - sPt.x), width, height );
		return getDiamondPoint( sPt, calcAngle(sPt, ePt), ePt, width, height );
	}
	else {
		return getRectPoint( sPt, calcAngle(sPt, ePt), width, height );
	}
}

/**
 * Check to see if the given point is within the provided Rectangle given world coordinates.
 * @param {PIXI.Point} point
 * @param {PIXI.Rectangle} rect
 */
export function containsWorldPoint(point: Point | Rectangle, rect: Rectangle): boolean {
	return rect.contains(point.x, point.y);
}

export function overlapRect(rec1: Rectangle, rec2: Rectangle): boolean {
	if (rec2.width === Infinity) { return (rec1.top < rec2.bottom && rec1.bottom > rec2.top); }
	if (rec2.height === Infinity) { return (rec1.left < rec2.right && rec1.right > rec2.left); }
	return (rec1.left < rec2.right && rec1.right > rec2.left &&
		rec1.top < rec2.bottom && rec1.bottom > rec2.top );
}

/**
 * Return a list of tickets within the given region. Coordinates should be in _world_ coordinates.
 *
 * If region width is Infinity, then it will only do a Y-axes check on the ticket
 * If region height is Infinity, then it will only do a X-axes check on the ticket
 *
 * TODO: MAKE GENERIC VERSION IF EVER NEEDED:
 * - Inclusion types:
 * -- entirely overlapping
 * -- partially overlapping
 * -- more than half overlapping
 * - Make generic
 * -- (Accept callback which returns an object containing X, Y, width, height?)
 * @param {Ticket[]} tickets The source ticket list
 * @param {PIXI.Rectangle} region The region that has to overlap the ticket
 */
export function getTicketsInRect(tickets: ListLike<Ticket>, region: Rectangle) {
	// If region.width and region.height are both Infinity...just return tickets.
	// - this check makes typing rather obnoxious, it's also not particularly useful, so..... just going to comment it out
	// if (region.width === Infinity && region.height === Infinity){return tickets}
	const output: Ticket[] = [];

	// Create one Rectangle then edit the values for each ticket.
	// Don't want to create a new Rectangle instance for each ticket when it'll just be eaten in the end by GC anyway
	const scratchRect: Rectangle = new Rectangle(0, 0, 1, 1);

	tickets.forEach((ticket: Ticket) => {
		// Populate rect and point values
		scratchRect.x = ticket.view.left;
		scratchRect.y = ticket.view.top;
		scratchRect.width = ticket.view.width;
		scratchRect.height = ticket.view.height;
		if (overlapRect(scratchRect, region)) {
			output.push(ticket);
		}
	});

	return output;
}

export function moveContainerToNewLayer(newLayer: FancyContainer, childToMove: FancyContainer): void {
	// Remove _thing_ from the parent
	const parent = childToMove.parent;
	if (parent) {
		parent.removeChild(childToMove);

		// Add _thing_ to new container
		newLayer.addChild(childToMove);
	}
}

export function calcBoundingRectFromTickets(tickets) {
	let left = Number.POSITIVE_INFINITY;
	let right = 0;
	let top = Number.POSITIVE_INFINITY;
	let bottom = 0;

	tickets.forEach((ticket: Ticket) => {
		left = Math.min(left, ticket.view.left);
		right = Math.max(right, ticket.view.left + ticket.view.width);
		top = Math.min(top, ticket.view.top);
		bottom = Math.max(bottom, ticket.view.top + ticket.view.height);
	});

	return new Rectangle(left, top, right - left, bottom - top);
}
